import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Avatar,
  TextField,
  Button,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  Paper,
  Divider,
  Stack,
} from '@mui/material';
import { PhotoCamera } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import {
  getSessionProfile,
  getRegistryTenantIdFromSession,
  cacheLoginJobPosition,
} from 'shared/utils/sessionProfileStore';
import useUserService from 'shared/api/services/userService';
import {
  getTenantUserById,
  updateTenantUserRecord,
} from 'shared/api/services/tenantUserService';
import EditPictureDialog from './EditPictureDialog';
import ChangePasswordDialog from './ChangePasswordDialog';
import Header from 'shared/components/Header';
import ClockLoader from 'shared/components/ClockLoader';
import { toast } from 'react-hot-toast';
import useProfilePhotoSrc from 'shared/hooks/useProfilePhotoSrc';
import { useAuth } from 'shared/hooks/useAuth';

const ROLE_OPTIONS = ['USER', 'ADMIN', 'SYSTEMADMIN', 'MANAGER', 'SUPPLIER'];

const roleAsString = (r) => (typeof r === 'string' ? r : r?.name ?? 'USER');

const statusAsString = (s) =>
  typeof s === 'string' ? s : s?.name ?? 'ACTIVE';

const resolveRegistryTenantId = () => {
  const fromSession = getRegistryTenantIdFromSession();
  if (fromSession) return fromSession;
  const env = process.env.REACT_APP_TENANT_ID;
  if (env != null && String(env).trim() !== '') return String(env).trim();
  const u = getSessionProfile();
  const t = u?.tenantId;
  if (t != null && String(t).trim() !== '') return String(t).trim();
  return null;
};

const ProfileDetails = () => {
  const { userId: routeUserId } = useParams();
  const [userData, setUserData] = useState(null);
  const [changeImageDialogOpen, setChangeImageDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const userService = useUserService();
  const { user } = useAuth();

  const sessionProfile = getSessionProfile();
  const id = routeUserId || sessionProfile?.id || user?.data?.id;
  const registryTenantId = resolveRegistryTenantId();

  const avatarPhotoSrc = useProfilePhotoSrc(userData?.photoUrl);

  const loadUserDetails = useCallback(async () => {
    if (!id) {
      setPageLoading(false);
      return;
    }
    setPageLoading(true);
    try {
      let data = null;
      if (registryTenantId) {
        try {
          data = await getTenantUserById(registryTenantId, id);
        } catch {
          data = await userService.loadUserDetails(id);
        }
      } else {
        data = await userService.loadUserDetails(id);
      }
      if (!data) {
        throw new Error('No user data');
      }
      setUserData({
        ...data,
        role: roleAsString(data?.role),
        tenantName: data?.tenantName ?? '',
        status: statusAsString(data?.status),
        photoUrl: data?.photoUrl ?? '',
        tenantId: data?.tenantId ?? registryTenantId,
      });
    } catch (error) {
      console.error('Failed to load user details', error);
      toast.error(error.response?.data?.message || 'Failed to load user details');
    } finally {
      setPageLoading(false);
    }
    // userService.loadUserDetails is stable enough for our fallback; omit from deps to avoid loops
    // eslint-disable-next-line react-hooks/exhaustive-deps -- userService identity changes each render
  }, [id, registryTenantId]);

  useEffect(() => {
    loadUserDetails();
  }, [loadUserDetails]);

  const handleChange = (event) => {
    setUserData((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  };

  const handleSaveAll = async () => {
    const updateUserDetailsDto = {
      name: userData.name,
      email: userData.email,
      role: roleAsString(userData.role),
      phoneNumber: userData.phoneNumber,
      jobPosition: userData.jobPosition,
      tenantName: userData.tenantName?.trim() ?? '',
    };

    setSaving(true);
    try {
      if (registryTenantId && userData?.id) {
        await updateTenantUserRecord(registryTenantId, {
          id: userData.id,
          name: userData.name,
          email: userData.email,
          phoneNumber: userData.phoneNumber ?? '',
          jobPosition: userData.jobPosition ?? '',
          role: roleAsString(userData.role),
          tenantName: (userData.tenantName ?? '').trim(),
          tenantId: userData.tenantId || registryTenantId,
          status: userData.status || 'ACTIVE',
          photoUrl: userData.photoUrl || null,
          verifiedAt: userData.verifiedAt,
        });
        toast.success('Profile updated (tenant)');
      } else {
        await userService.updateUserDetails(id, updateUserDetailsDto);
        toast.success('Profile updated');
      }
      cacheLoginJobPosition({
        id: userData.id,
        email: userData.email,
        name: userData.name,
        jobPosition: userData.jobPosition,
      });
      await loadUserDetails();
    } catch (error) {
      console.error('Error updating user details:', error);
      toast.error(error.response?.data?.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
    },
  };

  if (pageLoading) {
    return (
      <Container
        maxWidth="md"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
        }}
      >
        <ClockLoader size={56} />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Header title="User Details" />

      <Paper
        elevation={3}
        sx={{
          mt: 2,
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        {userData && (
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Grid
              container
              spacing={3}
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid item xs={12} md>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  alignItems={{ xs: 'center', sm: 'center' }}
                >
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <Avatar
                      alt={userData.name || 'Profile Picture'}
                      src={avatarPhotoSrc}
                      sx={{
                        width: 110,
                        height: 110,
                        border: userData.photoUrl
                          ? '2px solid #d1d5db'
                          : '2px solid #F5272E',
                        boxShadow: 2,
                      }}
                    />
                    <Tooltip title="Choose image">
                      <IconButton
                        color="primary"
                        component="label"
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          right: 0,
                          backgroundColor: '#657073',
                          color: '#fff',
                          '&:hover': {
                            backgroundColor: '#4f585b',
                          },
                        }}
                        onClick={() => setChangeImageDialogOpen(true)}
                      >
                        <PhotoCamera />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                    <Typography variant="h5" fontWeight={700} gutterBottom>
                      {userData.name || 'User Profile'}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {userData.email || 'No email'}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Company:</strong> {userData.tenantName}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Phone:</strong> {userData.phoneNumber || '-'}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} md="auto">
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: { xs: 'center', md: 'flex-end' },
                  }}
                >
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => setPasswordDialogOpen(true)}
                    sx={{
                      minWidth: 180,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                  >
                    Change Password
                  </Button>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" fontWeight={700} gutterBottom>
              General Information
            </Typography>

            <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="User Name"
                  variant="outlined"
                  name="name"
                  value={userData.name || ''}
                  onChange={handleChange}
                  sx={fieldSx}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  variant="outlined"
                  name="email"
                  value={userData.email || ''}
                  onChange={handleChange}
                  sx={fieldSx}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  variant="outlined"
                  name="phoneNumber"
                  value={userData.phoneNumber || ''}
                  onChange={handleChange}
                  sx={fieldSx}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Job Position"
                  variant="outlined"
                  name="jobPosition"
                  value={userData.jobPosition || ''}
                  onChange={handleChange}
                  sx={fieldSx}
                />
              </Grid>

            </Grid>

            <Box
              sx={{
                mt: 3,
                display: 'flex',
                justifyContent: { xs: 'center', sm: 'flex-end' },
              }}
            >
              <Button
                variant="contained"
                color="secondary"
                onClick={handleSaveAll}
                disabled={saving}
                sx={{
                  minWidth: 160,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                }}
              >
                {saving ? <ClockLoader inline size={22} /> : 'Save All'}
              </Button>
            </Box>
          </Box>
        )}

        <EditPictureDialog
          open={changeImageDialogOpen}
          onClose={() => setChangeImageDialogOpen(false)}
          loadUserDetails={loadUserDetails}
          profileUserId={id}
          registryTenantId={registryTenantId}
        />

        <ChangePasswordDialog
          open={passwordDialogOpen}
          onClose={() => setPasswordDialogOpen(false)}
          onSuccess={() => {
            setPasswordDialogOpen(false);
            loadUserDetails();
          }}
        />
      </Paper>
    </Container>
  );
};

export default ProfileDetails;
